import { Link } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import { Coffee } from 'lucide-react'
import { SectionLabel } from '@/frontend/features/Home/SectionLabel'
import { PanelEmptyState } from '@/frontend/components/EmptyState'
import type { FailureItem, FeedEvent, WorkerFeedPayload } from '@/frontend/features/Home/types'

interface Props {
  workerName: string
  feed?: WorkerFeedPayload
}

const MAX_ROWS = 6

// Hora compacta da coluna mono: hoje → HH:MM, ontem → "ontem", antes → dd/mm.
function activityTimeLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const dayDelta = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000)
  if (dayDelta === 0) return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  if (dayDelta === 1) return 'ontem'
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

// Frase de operação por tipo de evento. Fontes reais (HomeFeed#worker_recap):
// artifact publicado, conversa arquivada, alerta de eval. O chip só afirma o
// que o dado sustenta: publicado/resolvido = ok, alerta = atenção — nunca um
// status de execução fabricado.
function eventPhrase(event: FeedEvent): { phrase: React.ReactNode; chip: React.ReactNode } {
  switch (event.kind) {
    case 'publicacao':
      return {
        phrase: (
          <>
            Publicou <b className="font-semibold text-foreground">{event.title}</b>
          </>
        ),
        chip: <Badge variant="success" className="flex-none">ok</Badge>
      }
    case 'conversa_resolvida':
      return {
        phrase: (
          <>
            Resolveu <b className="font-semibold text-foreground">{event.title}</b>
          </>
        ),
        chip: <Badge variant="success" className="flex-none">ok</Badge>
      }
    case 'atencao':
      return {
        phrase: <span className="text-foreground">{event.title}</span>,
        chip: <Badge variant="warning" className="flex-none">atenção</Badge>
      }
    default:
      return {
        phrase: event.title,
        chip: <Badge variant="secondary" className="flex-none">—</Badge>
      }
  }
}

const ROW =
  'flex w-full items-center gap-3 border-b border-border px-4 py-[11px] text-left last:border-b-0'
const TIME = 'w-[44px] flex-none font-mono text-[10.5px] text-faint-foreground'

/**
 * "O que a {Nome} fez" (Iteração 3, Onda B): o painel de atividade do
 * dashboard de operação — substitui o "Enquanto você esteve fora" da
 * mini-home com a MESMA fonte (worker_feed = HomeFeed#worker_recap escopado),
 * reenquadrada como operação: linhas hora mono + frase + chip ok/atenção.
 * Falhas acionáveis entram primeiro e navegam; eventos são leitura do que o
 * agente fez (o Ajuste D tirou o composer da Visão geral, então os eventos
 * deixaram de pré-preencher — a conversa mora na aba Chat agora).
 */
export function WorkerActivityPanel({ workerName, feed }: Props) {
  const firstName = workerName.trim().split(/\s+/)[0] || workerName
  const failures = (feed?.failures ?? []).slice(0, MAX_ROWS)
  const events = (feed?.events ?? []).slice(0, Math.max(0, MAX_ROWS - failures.length))

  return (
    <section aria-label={`O que ${firstName} fez`} className="space-y-2" data-testid="worker-activity-panel">
      <SectionLabel>O que {firstName} fez</SectionLabel>

      {failures.length === 0 && events.length === 0 ? (
        <PanelEmptyState icon={Coffee} title="Dia tranquilo até agora" description="Nada pra reportar." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {failures.map((failure: FailureItem) => (
            <Link
              key={failure.id}
              href={failure.action_path}
              className={`${ROW} transition-colors hover:bg-accent`}
            >
              <span className={TIME}>agora</span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
                {failure.title}
              </span>
              <Badge variant="critical" className="flex-none">falhou</Badge>
            </Link>
          ))}
          {events.map((event: FeedEvent) => {
            const { phrase, chip } = eventPhrase(event)
            return (
              <div key={event.id} className={ROW}>
                <span className={TIME}>{activityTimeLabel(event.happened_at)}</span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">{phrase}</span>
                {chip}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

/** Skeleton com a forma do painel (3 linhas) enquanto o worker_feed chega. */
export function WorkerActivityPanelSkeleton({ workerName }: { workerName: string }) {
  const firstName = workerName.trim().split(/\s+/)[0] || workerName
  return (
    <section aria-label={`O que ${firstName} fez`} className="space-y-2">
      <SectionLabel>O que {firstName} fez</SectionLabel>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 border-b border-border px-4 py-[11px] last:border-b-0">
            <div className="h-3 w-[44px] flex-none animate-pulse rounded bg-muted" />
            <div className="min-w-0 flex-1">
              <div className="h-3.5 w-3/5 animate-pulse rounded bg-muted" />
            </div>
            <div className="h-5 w-10 flex-none animate-pulse rounded-full bg-muted" />
          </div>
        ))}
      </div>
    </section>
  )
}
